import { useId, type ReactNode } from 'react'
import { Skeleton } from '../../platform/async/Skeleton'
import { Money } from '../../platform/primitives/Money'
import { HeroTile, Panel } from '../../platform/primitives/Panel'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { priorPhrase } from './cashflowPeriod'
import { deltaPct, netDelta, shortDelta } from './cashflowRules'
import type { Cashflow } from './cashflowWire'

/** One tile: a label naming it, the figure, and a sub line. The drill-down page's stats use it too. */
export function Tile({ hero = false, label, figure, sub }: { hero?: boolean; label: string; figure: ReactNode; sub?: ReactNode }) {
  const id = useId()
  const Surface = hero ? HeroTile : Panel
  return (
    <Surface role="listitem" aria-labelledby={id} className={`cf-tile${hero ? ' cf-tile--net' : ''}`}>
      <span id={id} className="cf-tile__label">
        {label}
      </span>
      <span className="cf-tile__figure-row">{figure}</span>
      <span className="cf-tile__sub">{sub}</span>
    </Surface>
  )
}

/**
 * The selected period's four tiles: Net (the one HeroTile, rule 9), In, Out
 * and Transfers, each against the period before. Every figure is the server's
 * `totals`; the only arithmetic is `netDelta` and `deltaPct`, on two server
 * figures. The comparison caption comes from the prior's own dates, so a
 * partial prior reads "vs 1–12 Sep", never "vs September".
 *
 * Columns from `useBreakpoint()` as a class (rule 5): four across where there
 * is room, two by two below.
 */
export function PeriodTiles({ data }: { data: Cashflow }) {
  const bp = useBreakpoint()
  const { currency, totals, prior } = data
  const phrase = prior === null ? null : priorPhrase(prior, data.granularity)
  const delta = netDelta(totals, prior)
  const pctLine = (current: number, before: number | undefined) =>
    phrase === null || before === undefined ? null : shortDelta(deltaPct(current, before), phrase)

  return (
    <div role="list" aria-label="Cashflow totals" className={`cf-tiles cf-tiles--four cf-tiles--${bp}`}>
      <Tile
        hero
        label="Net"
        figure={
          <span className="cf-tile__figure">
            <Money cents={totals.netCents} currency={currency} signed display="whole" />
          </span>
        }
        sub={
          delta === null || phrase === null ? null : delta === 0 ? (
            `No change ${phrase}`
          ) : (
            <>
              {/* The arrow carries the direction, so it is named rather than hidden: the amount after it is unsigned. */}
              <span role="img" aria-label={delta < 0 ? 'Down' : 'Up'}>
                {delta < 0 ? '▼' : '▲'}
              </span>{' '}
              <Money cents={Math.abs(delta)} currency={currency} display="whole" /> {phrase}
            </>
          )
        }
      />
      <Tile
        label="In"
        figure={
          <span className="cf-tile__figure">
            <Money cents={totals.incomeCents} currency={currency} display="whole" />
          </span>
        }
        sub={pctLine(totals.incomeCents, prior?.incomeCents)}
      />
      <Tile
        label="Out"
        figure={
          <span className="cf-tile__figure">
            <Money cents={totals.expenseCents} currency={currency} display="whole" />
          </span>
        }
        sub={pctLine(totals.expenseCents, prior?.expenseCents)}
      />
      <Tile
        label="Transfers"
        figure={
          // Two figures, never one: a transfer is neither earned nor spent, and
          // out + in (or out − in) is a number the server never sent.
          <span className="cf-tile__figure cf-tile__figure--pair">
            <span className="cf-tile__part">
              <Money cents={totals.transfersOutCents} currency={currency} display="whole" /> out
            </span>
            <span className="cf-tile__dot" aria-hidden="true">
              {' · '}
            </span>
            <span className="cf-tile__part">
              <Money cents={totals.transfersInCents} currency={currency} display="whole" /> in
            </span>
          </span>
        }
        sub="Between your own accounts"
      />
    </div>
  )
}

/** The loading shape: the same grid, a label, figure and sub-line bar per tile. */
export function PeriodTilesSkeleton() {
  const bp = useBreakpoint()
  return (
    <div className={`cf-tiles cf-tiles--four cf-tiles--${bp}`} aria-hidden="true">
      {['net', 'in', 'out', 'transfers'].map((k) => (
        <Panel key={k} className="cf-tile">
          <Skeleton height={10} width="45%" />
          <Skeleton height={26} width="60%" />
          <Skeleton height={10} width="40%" />
        </Panel>
      ))}
    </div>
  )
}
